import React, { useEffect, useState } from 'react'
import { flowRight as compose, isEmpty } from 'lodash'
import { useSetRecoilState } from 'recoil'
import qs from 'querystring'
import type { RouteChildrenProps } from 'react-router-dom'
import { addHours, setMinutes, setHours, setSeconds } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { withBackOfficeCms } from 'helpers/hocs'
import { addSearchParams, parseDate } from 'helpers/common'
import { Breadcrumb, breadcrumbState } from 'recoils/atoms'
import {
  useFindAllUserTasks,
  TicketAssignSearchAndFilter,
  TicketCalendar,
  TicketCalendarSingle,
  TicketCalendarLoading,
  TicketCalendarNotFound,
} from 'features/Ticket'
import type {
  IParamTicketAssignment,
  ISetSearchParams,
  IUser,
} from 'features/Ticket'
import { Thumbnail } from 'components'

const TicketAssignment: React.FC<RouteChildrenProps<{ id: string }>> = ({
  history,
  location,
  match,
}: RouteChildrenProps<{ id: string }>) => {
  const { t } = useTranslation('layout')

  const base = 'con-ticket-calendar'
  const totalHours = 24

  const { search, pathname } = location
  const searchParams: IParamTicketAssignment = qs.parse(search.replace('?', ''))
  const date = parseDate(searchParams.date) || new Date()
  const id = match?.params.id
  const setBreadcrumbs = useSetRecoilState(breadcrumbState)
  const [{ data: users, loading }] = useFindAllUserTasks(searchParams)

  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([])

  const handleSetSelectedUsers = (checked: boolean, user: IUser): void => {
    if (!checked) {
      const nextSelectedUsers = selectedUsers.filter(
        (selectedUser) => selectedUser.id !== user.id,
      )

      return setSelectedUsers([...nextSelectedUsers])
    }

    return setSelectedUsers([...selectedUsers, user])
  }

  const dateHours: Date[] = Array(totalHours)
    .fill(setHours(setMinutes(setSeconds(date, 0), 0), 0))
    .map((date, incrementor) => addHours(date, incrementor))

  const setSearchParams: ISetSearchParams = (params) => {
    const nextParams = addSearchParams(searchParams, params)

    history.replace({
      pathname,
      search: qs.stringify(nextParams),
    })
  }

  useEffect(() => {
    const _breadcrumbs: Breadcrumb[] = [
      { key: `/ticket/${id}/detail`, label: t('ticketDetail.breadcrumb') },
      {
        key: `/ticket/${id}/assign`,
        label: (
          <div className={`${base}__breadcrumb-con`}>
            <span>{t('assignTicket.breadcrumb')}</span>
            <Thumbnail className={`${base}__breadcrumb-thumbnail`} />
          </div>
        ),
      },
    ]

    setBreadcrumbs(_breadcrumbs)

    return () => {
      setBreadcrumbs([])
    }
  }, [])

  const onSubmit = () => {
    // NOTE: Implement pass data to ModalAssignment here.
  }

  return (
    <div className={base}>
      <TicketAssignSearchAndFilter
        searchParams={searchParams}
        setSearchParams={setSearchParams}
      />
      <TicketCalendar dateHours={dateHours}>
        {loading ? (
          <TicketCalendarLoading />
        ) : !users ? (
          <TicketCalendarNotFound />
        ) : (
          users.map((user, index) => (
            <TicketCalendarSingle
              key={index}
              dateHours={dateHours}
              user={user}
              checked={Boolean(
                selectedUsers.find((_user) => user.id === _user.id),
              )}
              shouldEventTagGray={
                !isEmpty(selectedUsers) &&
                !selectedUsers.find((_user) => user.id === _user.id)
              }
              onSelectUser={handleSetSelectedUsers}
              onBlockClick={() => onSubmit()}
            />
          ))
        )}
      </TicketCalendar>
    </div>
  )
}

export default compose(withBackOfficeCms)(TicketAssignment)
